'use client';

import { createContext, useCallback, useContext, useMemo, useRef, type ReactNode } from 'react';
import type { ErrorCategory } from '@/lib/analytics/errorCategory';
import type { PaymentMethodCategory, PaymentProvider } from '@/lib/analytics/events';

export type PaymentOutcome = {
  provider: PaymentProvider;
  methodCategory: PaymentMethodCategory;
  /** The last failure seen in this dialog, or `none` if there was none. */
  errorCategory: ErrorCategory;
};

// `ach` matches the dialog's own default method selection.
const INITIAL_OUTCOME: PaymentOutcome = {
  provider: 'unknown',
  methodCategory: 'ach',
  errorCategory: 'none',
};

type PaymentOutcomeContextValue = {
  report: (partial: Partial<PaymentOutcome>) => void;
  read: () => PaymentOutcome;
};

const PaymentOutcomeContext = createContext<PaymentOutcomeContextValue | undefined>(undefined);

/**
 * Collects what the terminal payment-method event needs from the three places
 * that know it, so one outcome event fires on close instead of one per failed
 * attempt — which would make the funnel's outcome count exceed its start count.
 *
 * Held in a ref, not state: nothing renders from it, and a retry loop must not
 * re-render the payment form it is reporting on.
 */
export const PaymentOutcomeProvider = ({ children }: { children: ReactNode }) => {
  const outcomeRef = useRef<PaymentOutcome>(INITIAL_OUTCOME);

  const report = useCallback((partial: Partial<PaymentOutcome>) => {
    outcomeRef.current = { ...outcomeRef.current, ...partial };
  }, []);

  const read = useCallback(() => outcomeRef.current, []);
  const value = useMemo(() => ({ report, read }), [report, read]);

  return <PaymentOutcomeContext value={value}>{children}</PaymentOutcomeContext>;
};

export const usePaymentOutcome = (): PaymentOutcomeContextValue => {
  const value = useContext(PaymentOutcomeContext);

  if (!value) {
    throw new Error('usePaymentOutcome must be used inside a PaymentOutcomeProvider.');
  }

  return value;
};
